import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from '@mui/material';
import {
  ContentCopyOutlined as CopyIcon,
  CheckOutlined as CopiedIcon,
  SendOutlined as SendIcon,
} from '@mui/icons-material';

import HiringService, { CandidateMessage, CandidateMessages } from '../../api/services/hiring.service';
import { isApple, openSms, smsUrl } from './messageLinks';

/**
 * The message to send, already written.
 *
 * The pilot ran its whole pipeline on six text messages kept in a doc and
 * retyped by hand — with the wrong name in them often enough to be a
 * running joke. Retyping is the smaller half of the problem: the right
 * message is a function of where the person has got to, and working that
 * out at 7am is how a background-check nudge goes out three days late.
 *
 * So the server decides which one is due and fills in the names, the rate
 * and the dates. This screen presents that one first, keeps the other five
 * one click away — the engine is not always right, and a person who has
 * just spoken to the candidate knows something the record does not — and
 * lets the text be edited before it is copied, because the last word
 * belongs to whoever is actually sending it.
 *
 * Copying records NOTHING. It was briefly treated as the send — the
 * message does leave from somebody's own phone, so there is no later step
 * to hang it on — but that makes reading the wording back an act with
 * side effects, and a checkpoint that ticks itself because somebody looked
 * is worse than one nobody ticked. The checklist is where a step is
 * marked done.
 */

interface Props {
  candidateId: string;
  candidateName: string;
  phone?: string | null;
  open: boolean;
  onClose: () => void;
}

const MessageComposer: React.FC<Props> = ({
  candidateId, candidateName, phone, open, onClose,
}) => {
  const [data, setData] = useState<CandidateMessages | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Set when the Messages hand-off went nowhere: this computer has no app
  // that takes text-message links.
  const [noMessages, setNoMessages] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setCopied(false);
    setError(null);
    HiringService.messages(candidateId)
      .then((result) => {
        setData(result);
        // The one that fits, or the first one — never an empty pane
        // waiting to be told what to show.
        const pick = result.suggested || result.messages[0]?.key || null;
        setSelected(pick);
        setDraft(result.messages.find((m) => m.key === pick)?.body || '');
      })
      .catch(() => setError('The messages could not be loaded.'))
      .finally(() => setLoading(false));
  }, [open, candidateId]);

  const choose = useCallback((message: CandidateMessage) => {
    setSelected(message.key);
    setDraft(message.body);
    setCopied(false);
  }, []);

  const current = data?.messages.find((m) => m.key === selected) || null;
  // Asked of the DRAFT, not the template: somebody who has typed the
  // clinic's name in should not still be told to fill it in.
  const gaps = (draft.match(/\[[a-z ]+\]/g) || []);

  const copy = async () => {
    if (!current) return;
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
    } catch {
      setError('Copying failed — select the text and copy it by hand.');
      return;
    }
  };

  // Copied first, while the click still counts as a user action — by the
  // time we know nothing opened, the browser may no longer allow it, and
  // the text on the clipboard is the whole of the fallback.
  const send = async () => {
    if (!current || !draft.trim()) return;
    const onClipboard = await navigator.clipboard.writeText(draft).then(() => true, () => false);
    if (onClipboard) setCopied(true);
    const opened = await openSms(smsUrl(phone, draft, isApple()));
    if (!opened) setNoMessages(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Message {candidateName}
        {data && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {data.idleDays === 0
              ? 'Touched today'
              : `${data.idleDays} days since anybody touched this`}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ minHeight: 380 }}>
          {/* Which message. The one that fits is marked and sits where it
              already is in the sequence — reordering the list so it comes
              first would mean the same message moved every visit. */}
          <Box
            sx={{
              width: { xs: '100%', md: 260 },
              flexShrink: 0,
              borderRight: { md: '1px solid' },
              borderBottom: { xs: '1px solid', md: 'none' },
              borderColor: { xs: 'divider', md: 'divider' },
              py: 1,
            }}
          >
            {loading && (
              <Typography variant="body2" color="text.disabled" sx={{ p: 2 }}>Loading…</Typography>
            )}
            {data?.messages.map((message) => {
              const active = message.key === selected;
              return (
                <Box
                  key={message.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => choose(message)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(message); }
                  }}
                  sx={{
                    px: 2, py: 1.5, cursor: 'pointer',
                    borderLeft: '3px solid',
                    borderColor: active ? 'primary.main' : 'transparent',
                    bgcolor: active ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ fontWeight: active ? 600 : 400 }}>
                      {message.label}
                    </Typography>
                    {message.key === data.suggested && (
                      <Chip size="small" color="primary" label="Send this" sx={{ height: 18, fontSize: 10 }} />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {message.when}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* The text itself, editable. A template nobody can adjust gets
              pasted into a phone and adjusted there, which is where the
              typos come from. */}
          <Box sx={{ flex: 1, p: 2.5, minWidth: 0 }}>
            {current?.why && (
              <Typography variant="body2" color="primary.main" sx={{ mb: 1.5 }}>
                {current.why}
              </Typography>
            )}

            <TextField
              multiline
              fullWidth
              minRows={10}
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setCopied(false); }}
              inputProps={{ 'aria-label': 'Message text' }}
              sx={{ '& .MuiInputBase-input': { fontSize: 14, lineHeight: 1.6 } }}
            />

            {gaps.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Fill in {gaps.join(' and ')} before you send it — the record
                does not have {gaps.length > 1 ? 'those' : 'that'}.
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {copied && (
          <Typography variant="body2" color="text.secondary">
            Copied.
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="outlined"
          startIcon={copied ? <CopiedIcon /> : <CopyIcon />}
          disabled={!current || !draft.trim()}
          onClick={copy}
        >
          {copied ? 'Copied' : 'Copy message'}
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={<SendIcon />}
          disabled={!current || !draft.trim()}
          onClick={send}
        >
          Open in Messages
        </Button>
      </DialogActions>

      <Dialog open={noMessages} onClose={() => setNoMessages(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Can't open Messages</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This computer has no app for text messages.{' '}
            {copied
              ? 'The message is copied — paste it into a text to '
              : 'Copy the message and paste it into a text to '}
            {phone || candidateName} from your phone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoMessages(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default MessageComposer;
